import { useMemo } from "react";

import { SearchV2CandidateList } from "./candidates/SearchV2CandidateList";

import { Column, LoadingIndicator, Row, Text } from "@/components";

import { useGetCandidatesByIds } from "@/hooks/queries/useGetCandidatesByIds";
import { useGetSallyCandidates } from "@/hooks/queries/useGetSallyCandidates";
import {
  SallyCandidate,
  SallyState,
  SearchInterest,
  SearchInterestChangeOptions,
  SearchLastDecision,
  SessionShortlist,
} from "@/types";

export const SearchV2SallyMode = ({
  hasCustomerTopPriority,
  isAgentActive,
  jobLocation,
  keywords,
  onLastDecisionChange,
  onSearchInterestChange,
  onShortlisted,
  searchInterests,
  selectedStates,
}: {
  hasCustomerTopPriority: boolean;
  isAgentActive: boolean;
  jobLocation: string;
  keywords: string[];
  onLastDecisionChange?: (decision: SearchLastDecision | null) => void;
  onSearchInterestChange: (
    talentId: number,
    isInterested: boolean | null,
    options?: SearchInterestChangeOptions,
  ) => void;
  onShortlisted: (shortlist: SessionShortlist) => void;
  searchInterests: SearchInterest[];
  selectedStates: SallyState[];
}) => {
  /* Database CRUD Operations */
  // Read
  const sally = useGetSallyCandidates({ enabled: true });

  /* React Hooks */
  const selected = useMemo(() => new Set(selectedStates), [selectedStates]);
  const matching = useMemo<SallyCandidate[]>(
    () =>
      (sally.data?.candidates ?? []).filter((candidate) =>
        selected.has(candidate.sallyState),
      ),
    [sally.data, selected],
  );
  const userIds = useMemo(
    () => matching.map((candidate) => candidate.talentId),
    [matching],
  );
  // guac caps the returned list (top N by APR) but tallies counts over the
  // whole corpus, so counts exceeding the list length means candidates exist
  // that the list can never show — say so rather than looking broken.
  const totalWorked = useMemo(
    () =>
      Object.values(sally.data?.counts ?? {}).reduce(
        (sum, count) => sum + count,
        0,
      ),
    [sally.data],
  );
  const isCorpusCapped = totalWorked > (sally.data?.candidates.length ?? 0);

  const byIds = useGetCandidatesByIds({
    enabled: Boolean(sally.data),
    keywords,
    userIds,
  });

  // nova now honors id order itself when no terms are present, so with no
  // keywords this re-sort is a belt-and-braces identity pass — kept because it
  // also heals ordering if pages ever arrive from mixed cache generations.
  const ordered = useMemo(() => {
    const rows = byIds.data?.pages.flatMap((page) => page.results) ?? [];
    if (keywords.length > 0) return rows;
    const rank = new Map(userIds.map((id, index) => [id, index]));
    return [...rows].sort(
      (a, b) =>
        (rank.get(a.userId) ?? Number.MAX_SAFE_INTEGER) -
        (rank.get(b.userId) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [byIds.data, keywords, userIds]);

  /* Render */
  const render = () => {
    if (sally.isPending) {
      return <LoadingIndicator className="py-16" variant="large" />;
    }
    if (sally.isError) {
      return (
        <Text className="py-16 text-center text-sm text-slate-500">
          Couldn&apos;t load Sally&apos;s candidates for this job.
        </Text>
      );
    }

    return <Column className="gap-4">{renderList()}</Column>;
  };

  const renderList = () => {
    if (userIds.length === 0) {
      return (
        <Text className="py-16 text-center text-sm text-slate-500">
          No candidates in the selected statuses. Adjust Sally status under
          Filters.
        </Text>
      );
    }
    if (byIds.isPending) {
      return <LoadingIndicator className="py-16" variant="large" />;
    }
    if (byIds.isError) {
      return (
        <Text className="py-16 text-center text-sm text-slate-500">
          Couldn&apos;t load candidate details.
        </Text>
      );
    }
    if (ordered.length === 0) {
      return (
        <Text className="py-16 text-center text-sm text-slate-500">
          No matches for that search within these candidates.
        </Text>
      );
    }
    // "Showing 27 of 27" reads like a bug — collapse to a plain count once
    // everything is loaded, and only lead with "Showing X of Y" mid-scroll.
    const isEverythingLoaded = ordered.length >= userIds.length;
    return (
      <Column className="gap-3">
        <Row className="items-baseline gap-1.5 pt-3">
          <Text className="text-sm text-slate-700">
            {isEverythingLoaded
              ? `${String(ordered.length)} ${ordered.length === 1 ? "candidate" : "candidates"}`
              : `Showing ${String(ordered.length)} of ${String(userIds.length)}`}
          </Text>
          <Text className="text-sm font-normal text-slate-400">
            · {keywords.length > 0 ? "Best Match First" : "Strongest Fit First"}
          </Text>
          {isCorpusCapped ? (
            <Text className="truncate text-sm font-normal text-slate-400">
              · Sally worked {totalWorked} in total — this list holds the
              strongest matches; status counts include everyone.
            </Text>
          ) : null}
        </Row>
        <SearchV2CandidateList
          candidatesOverride={ordered}
          focusResetKey={selectedStates.join(",")}
          overridePaging={{
            fetchNextPage: () => {
              void byIds.fetchNextPage();
            },
            hasNextPage: byIds.hasNextPage,
            isFetchingNextPage: byIds.isFetchingNextPage,
          }}
          includePastSubmitted
          hasCustomerTopPriority={hasCustomerTopPriority}
          includeRejected
          isAgentActive={isAgentActive}
          location={jobLocation}
          onLastDecisionChange={onLastDecisionChange}
          onSearchInterestChange={onSearchInterestChange}
          onShortlisted={onShortlisted}
          query={keywords.join(", ")}
          searchInterests={searchInterests}
        />
      </Column>
    );
  };

  return render();
};
